import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/hooks/useTenant";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
  Bell, HeartPulse, MessageSquare, Dumbbell, X, Check, BellRing,
  UserPlus, ClipboardList, Loader2, ExternalLink, ChevronRight, User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  student_id: string | null;
  reference_id: string | null;
  read_at: string | null;
  created_at: string;
}

interface StudentGroup {
  key: string;
  name: string;
  notifications: Notification[];
  unreadCount: number;
  latestAt: string;
  studentId: string | null;
  photoUrl: string | null;
}

const typeIcons: Record<string, typeof Bell> = {
  pain_report: HeartPulse,
  new_message: MessageSquare,
  workout_pending_approval: Dumbbell,
  new_registration: UserPlus,
  new_anamnese: ClipboardList,
};

const typeColors: Record<string, string> = {
  pain_report: "text-destructive bg-destructive/10",
  new_message: "text-primary bg-primary/10",
  workout_pending_approval: "text-amber-400 bg-amber-500/10",
  new_registration: "text-emerald-400 bg-emerald-500/10",
  new_anamnese: "text-violet-400 bg-violet-500/10",
};

const typeLabels: Record<string, string> = {
  pain_report: "Dor reportada",
  new_message: "Mensagem",
  workout_pending_approval: "Treino pendente",
  new_registration: "Autocadastro",
  new_anamnese: "Nova anamnese",
};

function extractStudentName(n: Notification): string {
  // Try to extract name from title patterns like "Novo aluno: Maria" or "📋 Nova anamnese: João"
  const match = n.title.match(/:\s*(.+)$/);
  if (match) return match[1].trim();
  return n.title.slice(0, 20);
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [approvedResults, setApprovedResults] = useState<Record<string, { student_id: string; whatsapp_url: string | null }>>({});
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const { permission, subscribed, subscribe, loading: pushLoading } = usePushNotifications();
  const { toast } = useToast();

  const { data: notifications } = useQuery({
    queryKey: ["professor-notifications"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("professor_notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data || []) as Notification[];
    },
    enabled: !!tenantId,
    refetchInterval: 30000,
  });

  // Fetch student photos for avatar display
  const studentIds = useMemo(() => {
    if (!notifications?.length) return [];
    const ids = new Set<string>();
    for (const n of notifications) {
      if (n.student_id) ids.add(n.student_id);
    }
    return Array.from(ids);
  }, [notifications]);

  const { data: studentPhotos } = useQuery({
    queryKey: ["student-photos-notifications", studentIds],
    queryFn: async () => {
      if (!studentIds.length) return {};
      const { data } = await supabase
        .from("students")
        .select("id, photo_url")
        .in("id", studentIds);
      const map: Record<string, string | null> = {};
      for (const s of data || []) {
        map[s.id] = s.photo_url;
      }
      return map;
    },
    enabled: studentIds.length > 0,
  });

  useEffect(() => {
    if (!tenantId) return;
    const channel = supabase
      .channel("professor-notifications-realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "professor_notifications" }, () => {
        queryClient.invalidateQueries({ queryKey: ["professor-notifications"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [tenantId, queryClient]);

  // Group notifications by student
  const groups = useMemo(() => {
    if (!notifications?.length) return [];

    const map = new Map<string, StudentGroup>();

    for (const n of notifications) {
      const key = n.student_id || n.reference_id || `sys_${n.id}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          name: extractStudentName(n),
          notifications: [],
          unreadCount: 0,
          latestAt: n.created_at,
          studentId: n.student_id || n.reference_id,
          photoUrl: n.student_id ? (studentPhotos?.[n.student_id] ?? null) : null,
        });
      }

      const group = map.get(key)!;
      group.notifications.push(n);
      if (!n.read_at) group.unreadCount++;
      if (n.created_at > group.latestAt) group.latestAt = n.created_at;
    }

    // Sort: groups with unread first, then by latest notification time
    return Array.from(map.values()).sort((a, b) => {
      if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
      if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
      return new Date(b.latestAt).getTime() - new Date(a.latestAt).getTime();
    });
  }, [notifications]);

  const unreadCount = notifications?.filter((n) => !n.read_at).length || 0;

  const markAsRead = async (id: string) => {
    await (supabase as any)
      .from("professor_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id);
    queryClient.invalidateQueries({ queryKey: ["professor-notifications"] });
  };

  const markGroupRead = async (group: StudentGroup) => {
    const unreadIds = group.notifications.filter((n) => !n.read_at).map((n) => n.id);
    if (!unreadIds.length) return;
    await (supabase as any)
      .from("professor_notifications")
      .update({ read_at: new Date().toISOString() })
      .in("id", unreadIds);
    queryClient.invalidateQueries({ queryKey: ["professor-notifications"] });
  };

  const markAllRead = async () => {
    const unreadIds = notifications?.filter((n) => !n.read_at).map((n) => n.id) || [];
    if (!unreadIds.length) return;
    await (supabase as any)
      .from("professor_notifications")
      .update({ read_at: new Date().toISOString() })
      .in("id", unreadIds);
    queryClient.invalidateQueries({ queryKey: ["professor-notifications"] });
  };

  const handleEnablePush = async () => {
    const ok = await subscribe();
    if (ok) {
      toast({ title: "Push ativado", description: "Você receberá notificações em tempo real." });
    } else {
      toast({ title: "Não foi possível ativar push", description: "Abra pelo app publicado e tente novamente.", variant: "destructive" });
    }
  };

  const handleApproveAnamnese = async (n: Notification, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!n.reference_id || approvingId) return;

    setApprovingId(n.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approve-anamnese`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ anamnese_id: n.reference_id }),
        }
      );
      const result = await res.json();

      if (!res.ok) {
        if (result.already_processed) {
          toast({ title: "Aluno já cadastrado", description: "Esta anamnese já foi processada." });
          markAsRead(n.id);
        } else {
          toast({ title: "Erro", description: result.error || "Erro ao aprovar.", variant: "destructive" });
        }
        return;
      }

      setApprovedResults((prev) => ({
        ...prev,
        [n.id]: { student_id: result.student_id, whatsapp_url: result.whatsapp_url },
      }));

      toast({ title: `✅ ${result.student_name} cadastrado!`, description: "Credenciais enviadas e treino sendo gerado pela IA." });
      queryClient.invalidateQueries({ queryKey: ["professor-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["anamneses"] });
    } catch {
      toast({ title: "Erro de conexão", variant: "destructive" });
    } finally {
      setApprovingId(null);
    }
  };

  const handleNotificationAction = (n: Notification) => {
    markAsRead(n.id);
    setOpen(false);

    if (n.type === "new_message" && n.student_id) {
      navigate(`/student/${n.student_id}?tab=messages`);
    } else if (n.type === "workout_pending_approval") {
      navigate("/dashboard");
      setTimeout(() => window.dispatchEvent(new CustomEvent("switch-dashboard-tab", { detail: "alertas" })), 100);
    } else if (n.type === "pain_report" && n.student_id) {
      navigate(`/student/${n.student_id}?tab=anamnese`);
    } else if (n.type === "new_registration" && (n.student_id || n.reference_id)) {
      navigate(`/student/${n.student_id || n.reference_id}`);
    } else if (n.type === "new_anamnese" && approvedResults[n.id]) {
      navigate(`/student/${approvedResults[n.id].student_id}`);
    } else if (n.type === "new_anamnese" && n.reference_id) {
      navigate(`/admin/anamneses/${n.reference_id}`);
    } else if (n.student_id) {
      navigate(`/student/${n.student_id}`);
    }
  };

  // Get the primary icon for the group based on most important unread notification type
  const getGroupIcon = (group: StudentGroup) => {
    const priorityOrder = ["pain_report", "new_anamnese", "workout_pending_approval", "new_message", "new_registration"];
    const unreadTypes = group.notifications.filter((n) => !n.read_at).map((n) => n.type);
    const allTypes = group.notifications.map((n) => n.type);
    const types = unreadTypes.length ? unreadTypes : allTypes;
    
    for (const t of priorityOrder) {
      if (types.includes(t)) return t;
    }
    return allTypes[0] || "default";
  };

  return (
    <div className="relative">
      <Button
        size="sm"
        variant="ghost"
        className="h-8 px-2 text-muted-foreground hover:text-foreground relative"
        onClick={() => setOpen(!open)}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </motion.span>
        )}
      </Button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-10 z-50 w-[340px] sm:w-[380px] rounded-xl border border-border bg-card shadow-xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Notificações</h3>
                <div className="flex gap-1">
                  {unreadCount > 0 && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={markAllRead}>
                      <Check className="h-3 w-3 mr-1" />
                      Marcar tudo
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setOpen(false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Push enable banner */}
              {!subscribed && permission !== "denied" && (
                <div className="px-4 py-2.5 border-b border-border bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BellRing className="h-3.5 w-3.5 text-primary" />
                      <span className="text-[11px] text-muted-foreground">Receba alertas mesmo offline</span>
                    </div>
                    <Button size="sm" variant="default" className="h-6 text-[10px] px-2" onClick={handleEnablePush} disabled={pushLoading}>
                      Ativar Push
                    </Button>
                  </div>
                </div>
              )}

              {/* Grouped list */}
              <div className="max-h-[420px] overflow-y-auto">
                {!groups.length ? (
                  <div className="py-10 text-center">
                    <Bell className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma notificação</p>
                  </div>
                ) : (
                  groups.map((group) => {
                    const isExpanded = expandedGroup === group.key;
                    const primaryType = getGroupIcon(group);
                    const PrimaryIcon = typeIcons[primaryType] || User;
                    const primaryColor = typeColors[primaryType] || "text-muted-foreground bg-muted";
                    const latestNotification = group.notifications[0];

                    return (
                      <div key={group.key} className="border-b border-border/50 last:border-0">
                        {/* Student card header */}
                        <button
                          onClick={() => setExpandedGroup(isExpanded ? null : group.key)}
                          className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-accent/30 transition-colors ${
                            group.unreadCount > 0 ? "bg-primary/[0.03]" : ""
                          }`}
                        >
                          <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                            {group.photoUrl ? (
                              <img src={group.photoUrl} alt={group.name} className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full bg-gradient-to-br from-muted to-secondary flex items-center justify-center">
                                <span className="text-sm font-semibold text-muted-foreground">
                                  {group.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className={`text-sm font-medium truncate ${group.unreadCount > 0 ? "text-foreground" : "text-muted-foreground"}`}>
                                {group.name}
                              </p>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {group.unreadCount > 0 && (
                                  <span className="h-5 min-w-[20px] rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center px-1.5">
                                    {group.unreadCount}
                                  </span>
                                )}
                                <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground/50 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                              </div>
                            </div>
                            <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                              {typeLabels[latestNotification.type] || latestNotification.type}
                              {" · "}
                              {formatDistanceToNow(new Date(group.latestAt), { addSuffix: true, locale: ptBR })}
                            </p>
                          </div>
                        </button>

                        {/* Expanded notification details */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="bg-muted/30 border-t border-border/30">
                                {group.notifications.map((n) => {
                                  const Icon = typeIcons[n.type] || Bell;
                                  const color = typeColors[n.type] || "text-muted-foreground bg-muted";
                                  const isAnamneseAction = n.type === "new_anamnese" && !n.read_at && !approvedResults[n.id];
                                  const isApproved = !!approvedResults[n.id];
                                  const isApproving = approvingId === n.id;

                                  return (
                                    <div
                                      key={n.id}
                                      className={`px-4 pl-8 py-2.5 flex items-start gap-2.5 border-b border-border/20 last:border-0 ${
                                        !n.read_at ? "" : "opacity-60"
                                      } ${isAnamneseAction ? "" : "cursor-pointer hover:bg-accent/20"}`}
                                      onClick={() => {
                                        if (!isAnamneseAction) handleNotificationAction(n);
                                      }}
                                    >
                                      <div className="relative shrink-0 mt-0.5">
                                        <div className={`h-6 w-6 rounded-md flex items-center justify-center ${color}`}>
                                          <Icon className="h-3 w-3" />
                                        </div>
                                        {!n.read_at && (
                                          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary ring-1 ring-card" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-[11px] font-medium ${!n.read_at ? "text-foreground" : "text-muted-foreground"}`}>
                                          {typeLabels[n.type] || n.title}
                                        </p>
                                        {n.body && (
                                          <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                                        )}
                                        <p className="text-[9px] text-muted-foreground/50 mt-0.5">
                                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: ptBR })}
                                        </p>

                                        {/* Anamnese approve action */}
                                        {isAnamneseAction && (
                                          <div className="flex gap-2 mt-1.5">
                                            <Button
                                              size="sm"
                                              variant="default"
                                              className="h-6 text-[10px] px-2.5"
                                              disabled={isApproving}
                                              onClick={(e) => handleApproveAnamnese(n, e)}
                                            >
                                              {isApproving ? (
                                                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Cadastrando...</>
                                              ) : (
                                                <><Check className="h-3 w-3 mr-1" /> Aceitar aluno</>
                                              )}
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-6 text-[10px] px-2 text-muted-foreground"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpen(false);
                                                navigate(`/admin/anamneses/${n.reference_id}`);
                                              }}
                                            >
                                              Detalhes
                                            </Button>
                                          </div>
                                        )}

                                        {/* Post-approval */}
                                        {isApproved && (
                                          <div className="flex gap-2 mt-1.5">
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-6 text-[10px] px-2"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpen(false);
                                                navigate(`/student/${approvedResults[n.id].student_id}`);
                                              }}
                                            >
                                              <ExternalLink className="h-3 w-3 mr-1" /> Ver perfil
                                            </Button>
                                            {approvedResults[n.id].whatsapp_url && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-6 text-[10px] px-2 text-emerald-500 border-emerald-500/30"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  window.open(approvedResults[n.id].whatsapp_url!, "_blank");
                                                }}
                                              >
                                                📱 WhatsApp
                                              </Button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Group action: mark all read + go to profile */}
                                <div className="px-4 pl-8 py-2 flex gap-2">
                                  {group.unreadCount > 0 && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 text-[10px] px-2 text-muted-foreground"
                                      onClick={() => markGroupRead(group)}
                                    >
                                      <Check className="h-3 w-3 mr-1" /> Marcar como lido
                                    </Button>
                                  )}
                                  {group.studentId && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 text-[10px] px-2 text-muted-foreground"
                                      onClick={() => {
                                        setOpen(false);
                                        navigate(`/student/${group.studentId}`);
                                      }}
                                    >
                                      <ExternalLink className="h-3 w-3 mr-1" /> Ir ao perfil
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
